import store from "~/store";
import { DatabaseClient, getDatabaseClient } from '~/data/db/DatabaseClient';
import { resolveOfflineDb } from './offlineDatabase';
import QueryBuilder from "./queryBuilder";
import { ServiceDetails, ServiceDetailsTable } from "~/types";
import { diffByIdAndDataField } from '~/data/local/diffHelper';

export class ServiceOfflineService {
  constructor(private readonly databaseClient: DatabaseClient | null = getDatabaseClient()) {}

  private readonly TABLE_NAME = 'Service';
  private readonly qb: QueryBuilder = new QueryBuilder();

  private get SQLite() {
    return resolveOfflineDb(this.databaseClient) || null;
  }

  async reset(services: ServiceDetails[]): Promise<void> {
    const db = this.SQLite;
    if (!db) return Promise.resolve();

    const { services: servicesToAdd, deleteServices } = await this.checkServices(services);

    if (deleteServices.length === 0 && servicesToAdd.length === 0) return;

    return new Promise<void>((resolve, reject) => {
      db.transaction(
        (tx) => {
          if (deleteServices.length) {
            const { query, params } = this.qb.deleteIn(this.TABLE_NAME, "id", deleteServices);
            tx.executeSql(query, params);
          }
          if (servicesToAdd.length) {
            const { query, params } = this.qb.insertOrReplace(this.TABLE_NAME, servicesToAdd);
            tx.executeSql(query, params);
          }
        },
        (e) => reject(e),
        resolve
      );
    });
  }

  async checkServices(serviceDetails: ServiceDetails[]): Promise<{ services: ServiceDetailsTable[]; deleteServices: string[] }> {
    const db = this.SQLite;
    if (!db) return { services: [], deleteServices: [] };

    const { query, params } = this.qb.select(this.TABLE_NAME);

    return new Promise<{ services: ServiceDetailsTable[]; deleteServices: string[] }>((resolve, reject) => {
      db.executeSql(
        query,
        params,
        (resultSet) => {
          const newServices = serviceDetails.map((data) => ({
            id: data.id,
            machineId: data.machineId,
            data: JSON.stringify(data),
          }));

          const dbServices: ServiceDetailsTable[] = this.qb.getAllResult(resultSet);
          const { toAdd, toDeleteIds } = diffByIdAndDataField(dbServices, newServices);

          resolve({ services: toAdd, deleteServices: toDeleteIds });
        },
        (e) => reject(e)
      );
    });
  }

  async getService(id: string): Promise<ServiceDetails | undefined> {
    const db = this.SQLite;
    if (!db) return undefined;

    const where = [{ name: 'id', value: id }];
    const { query, params } = this.qb.select(this.TABLE_NAME, [], where);

    return new Promise<ServiceDetails | undefined>((resolve, reject) => {
      db.executeSql(
        query,
        params,
        (result) => {
          const service = this.qb.getSingleResult(result);
          if (!service) return reject(new Error(`Service with id ${id} not found`));

          const data: ServiceDetails = {
            ...JSON.parse(service.data),
            id: service.id
          };

          resolve(data);
        },
        (e) => reject(e)
      );
    });
  }

  updateServiceDetail(service: ServiceDetails): void {
    const db = this.SQLite;
    if (!db) return;

    if (!store.getState().machines.isOfflineAvailable(service.machineId)) return;

    const { query, params } = this.qb.update(
      this.TABLE_NAME,
      {
        id: service.id,
        machineId: service.machineId,
        data: JSON.stringify(service)
      },
      [{ name: 'id', value: service.id }]
    );
    db.executeSql(query, params);
  }
}